import { useEffect, useState } from 'react';
import api from '@/lib/api';

// ────────────────────────────────────────────────────────────────────────────
// useTenantPlan — single source of truth for the current tenant's plan.
//
// Several pages (Developer, Audit Log, AI assistant, WhatsApp, etc.) need to
// know which features the tenant's plan includes so they can render an inline
// upsell instead of triggering the global 402 upgrade modal on mount.
//
// Naive `useEffect(() => api.get(...))` in every page would refetch the same
// /subscriptions/me payload on every navigation. So this hook keeps a tiny
// module-level cache + a pub/sub registry so all consumers share one fetch
// per session, but still re-render together when invalidate() is called
// (e.g. after a billing-page upgrade).
// ────────────────────────────────────────────────────────────────────────────

export interface TenantPlan {
  status?: string;
  plan?: { name: string; price: number; features?: string[]; effectiveFeatures?: string[] };
}

// `undefined` = never fetched yet, `null` = fetched and got nothing (free
// tenant with no subscription row, or 404). The distinction matters for
// `loading` — we want a spinner only on the first fetch.
let cached: TenantPlan | null | undefined = undefined;
let inflight: Promise<TenantPlan | null> | null = null;
const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((fn) => fn());
}

async function fetchPlan(): Promise<TenantPlan | null> {
  if (inflight) return inflight;
  inflight = api
    .get<TenantPlan>('/subscriptions/me')
    .then((r) => {
      cached = r.data ?? null;
      notify();
      return cached;
    })
    .catch(() => {
      cached = null;   // 404 / network — treat as "no subscription = Free"
      notify();
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Force a refetch — call after upgrade/downgrade so gated UI re-evaluates. */
export function invalidateTenantPlan() {
  cached = undefined;
  fetchPlan();
}

export function useTenantPlan() {
  const [, setTick] = useState(0);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    const sub = () => setTick((x) => x + 1);
    subscribers.add(sub);

    if (cached === undefined) {
      setLoading(true);
      fetchPlan().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => {
      subscribers.delete(sub);
    };
  }, []);

  const subscription = cached ?? null;
  // effectiveFeatures = plan.features[] ∪ module-derived (matches the backend
  // PlanFeatureGuard). Fall back to plain features[] for older API responses.
  const planFeatures = subscription?.plan?.effectiveFeatures ?? subscription?.plan?.features ?? [];
  // Until the first fetch resolves (cached === undefined) return '' rather than
  // 'Free' — otherwise plan displays flash "Free" before the real plan loads.
  // After the fetch, a tenant with no subscription correctly reads 'Free'.
  const planName = cached === undefined ? '' : (subscription?.plan?.name ?? 'Free');
  const hasFeature = (feature: string) => planFeatures.includes(feature);
  const isPaid = (subscription?.plan?.price ?? 0) > 0 && subscription?.status === 'active';

  return { subscription, planFeatures, planName, hasFeature, isPaid, loading };
}
